import {Button, Table, Tag, Alert, Tabs, Tooltip} from 'antd';
import {InfoCircleOutlined} from '@ant-design/icons';
import {
  attendingDinnerLabel,
  calculateProcessingFeeForRegistrations,
  EventRegistrationStatus,
  EventRegistrationType,
  formatCurrencyByLocalChapter,
  formatUtcToLocalDateTime,
  getPaidAmountIncludingProcessingFee,
  getStatusInfoOfEventRegistration,
  meetingPresentationLabel,
  roundCurrency,
  VENDOR_REP_CHANGE_CUTOFF_DAYS,
} from '../../../../../Util/commonUtility';
import dayjs from 'dayjs';
import {useNavigate} from 'react-router-dom';
import {useEffect, useState} from 'react';
import useLocalization from '../../../../../Localization';
import SponsorshipGroups from '../SponsorshipGroups';

// Ensure transaction_id is always a string or null (API may return object e.g. { id: 'pi_xxx' })
export const getTransactionIdString = (t) => {
  if (typeof t === 'string' && t && t !== 'null') return t;
  if (t && typeof t === 'object' && t.id) return t.id;
  return null;
};

const hasValidAttendeeDisplay = (attendee) => {
  const first = attendee?.attendee_first_name ?? attendee?.first_name;
  const last = attendee?.attendee_last_name ?? attendee?.last_name;
  const email = attendee?.attendee_email ?? attendee?.email;
  return !!(first?.trim?.() || last?.trim?.() || email?.trim?.());
};

const categorizeAndGroupByTransitionId = (attendees, ApplicationString) => {
  // My Events: only show paid (exclude To Be Paid and Payment Processing)
  const filteredAttendees = attendees?.filter(
    (attendee) =>
      attendee?.status !== EventRegistrationStatus.TO_BE_PAID &&
      attendee?.status !== EventRegistrationStatus.PAYMENT_PROCESSING,
  );

  const grouped = filteredAttendees?.reduce((acc, attendee) => {
    const transactionId = getTransactionIdString(attendee?.transaction_id);
    const key = transactionId ?? 'null';
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(attendee);
    return acc;
  }, {});

  return Object.keys(grouped)
    ?.filter((key) => key !== 'null' || grouped[key]?.length > 0)
    ?.map((key, index) => ({
      key,
      label: ApplicationString['userEventDetailsModal.payment'] + (index + 1),
      attendees: grouped[key],
    }));
};

const filterAttendeesByStatus = (sponsors, status = 1) => {
  return sponsors?.filter((attendee) => attendee?.status === status);
};

const filterCartStatusRegistrations = (list) => {
  return list?.filter(
    (item) =>
      hasValidAttendeeDisplay(item) &&
      (item?.status === EventRegistrationStatus.TO_BE_PAID ||
        item?.status === EventRegistrationStatus.PAYMENT_PROCESSING),
  );
};

const splitCartRegistrationsByStatus = (list) => {
  const paymentProcessing =
    list?.filter(
      (item) =>
        item?.status === EventRegistrationStatus.PAYMENT_PROCESSING &&
        hasValidAttendeeDisplay(item),
    ) || [];
  const toBePaid =
    list?.filter(
      (item) =>
        item?.status === EventRegistrationStatus.TO_BE_PAID &&
        hasValidAttendeeDisplay(item),
    ) || [];
  return {paymentProcessing, toBePaid};
};

const groupCartPaymentProcessingRegistrations = (list, ApplicationString) => {
  const grouped = (list || []).reduce((acc, attendee) => {
    const transactionId = getTransactionIdString(attendee?.transaction_id);
    const key = transactionId ?? 'null';
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(attendee);
    return acc;
  }, {});

  return Object.keys(grouped)
    .filter((key) => key !== 'null' || grouped[key]?.length > 0)
    .map((key, index) => ({
      key,
      label: ApplicationString['userEventDetailsModal.payment'] + (index + 1),
      attendees: grouped[key],
    }))
    .reverse();
};

const getRegistrationCosts = (registrations) => {
  const totalCost = (registrations || []).reduce((sum, attendee) => {
    return sum + parseFloat(attendee?.total_cost || 0);
  }, 0);
  const discountAmount = (registrations || []).reduce((sum, attendee) => {
    return sum + parseFloat(attendee?.discount_amount || 0);
  }, 0);

  return {
    totalCost,
    discountAmount,
    payableAmount: totalCost - discountAmount,
  };
};

const findSponsorshipEventOptionId = (registrations) =>
  registrations
    ?.map(
      (registration) =>
        registration?.event_registration_options?.sponsorship?.event_option_id ||
        registration?.event_option_id,
    )
    .find((eventOptionId) => eventOptionId !== undefined);

/** Resolve payment for the active tab (transaction) from event.payments. */
const resolveActiveTabPayment = (payments, activeTabKey) => {
  if (!payments?.length || activeTabKey == null || activeTabKey === '')
    return null;
  const match = (p) =>
    p?.transaction_key != null &&
    String(p.transaction_key) === String(activeTabKey);
  return payments.find(match) || null;
};

const resolveCartProcessingTabPayment = (payments, tabKey) => {
  if (!payments?.length) return null;
  if (tabKey != null && tabKey !== '' && String(tabKey) !== 'null') {
    const exact = resolveActiveTabPayment(payments, tabKey);
    if (exact) return exact;
  }
  return payments[0];
};

const getWaitingListAttendanceLabel = (record, ApplicationString) => {
  const raw = (record?.attendance_type ?? '').toString().trim().toLowerCase();
  const isWebcast = raw.includes('web');
  if (isWebcast) {
    return (
      ApplicationString['waitingList.attendanceType.webcast'] ||
      ApplicationString['editableCell.webcast'] ||
      'Webcast'
    );
  }
  return (
    ApplicationString['waitingList.attendanceType.inPerson'] ||
    ApplicationString['editableCell.inPerson'] ||
    'In person'
  );
};

const JAPAN_LOCAL_CHAPTER_NAME = 'japan';

const formatAttendeeFieldOrNA = (value) => {
  const trimmed = value == null ? '' : String(value).trim();
  return trimmed && trimmed.toLowerCase() !== 'null' ? trimmed : 'N/A';
};

/** Stripe/card paid amount > 0 — these registrations request a refund, not a cancel. */
export const isRefundableRegistration = (item) =>
  item?.status === EventRegistrationStatus.PAID &&
  Number(item?.paid_amount || 0) > 0;

/** $0 paid (free / no-charge / fee-waiver / credit-only) — cancel without a refund. */
export const isCancellableZeroPriceRegistration = (item) => {
  if (isRefundableRegistration(item)) {
    return false;
  }
  const totalCost = Number(item?.total_cost || 0);
  const discountAmount = Number(item?.discount_amount || 0);
  const creditAmount = Number(item?.credit_amount || 0);
  const paidAmount = Number(item?.paid_amount || 0);
  const netCost = totalCost - discountAmount - creditAmount;
  const isZeroPaidStatus =
    item?.status === EventRegistrationStatus.PAID && paidAmount <= 0;
  return (
    (item?.status === EventRegistrationStatus.NO_CHARGE_EVENT ||
      item?.status === EventRegistrationStatus.FEE_WAIVER ||
      isZeroPaidStatus ||
      netCost <= 0) &&
    item?.status !== EventRegistrationStatus.TO_BE_DELETED &&
    item?.status !== EventRegistrationStatus.PAYMENT_REFUNDED &&
    item?.status !== EventRegistrationStatus.TO_BE_PAID &&
    item?.status !== EventRegistrationStatus.PAYMENT_PROCESSING &&
    item?.status !== EventRegistrationStatus.REFUND_PENDING
  );
};

const EventDetailsModal = ({
  paidEvent,
  attendees,
  sponsors,
  eventRegistrationType,
  handleRefund,
  filteredAttendees,
  eventStartDate,
  eventId,
  eventType,
  eventLocalChapterData,
  event,
  onActiveTabAttendeesChange,
  onRefresh,
}) => {
  const {ApplicationString} = useLocalization();
  const navigate = useNavigate();
  const isJapanLocalChapter =
    eventLocalChapterData?.name?.toLowerCase() === JAPAN_LOCAL_CHAPTER_NAME;
  const [data, setData] = useState([]);
  const [activeTabKey, setActiveTabKey] = useState('');
  const [activeCartPaymentProcessingKey, setActiveCartPaymentProcessingKey] =
    useState('');
  const [transactionId, setTransactionId] = useState(null);
  const [activeTabOptionId, setActiveTabOptionId] = useState([]);
  const [activeTabCosts, setActiveTabCosts] = useState({
    payableAmount: 0,
    discountAmount: 0,
    totalCost: 0,
  });
  const [toBePaidEventRegistration, setToBePaidRegistration] = useState([]);
  const [eventRegistrationTypeData, setEventRegistrationTypeData] = useState(
    [],
  );
  // Paid sponsorships: one entry per sponsorship with each of its payments (original + added reps).
  const showSponsorshipGroups =
    eventRegistrationType === 'sponsorship' &&
    paidEvent &&
    event?.sponsorships?.length > 0;
  const refundMode = (isBankRefund, refund_status) => {
    if (refund_status) {
      return isBankRefund
        ? ApplicationString['userEventDetailsModal.refundMode.pbssCredit']
        : ApplicationString['userEventDetailsModal.refundMode.refundToBank'];
    } else {
      return ApplicationString[
        'userAddAttendeeComponent.eventRegistrationModel.n/a'
      ];
    }
  };

  const getProcessingFee = (registrations, payment) =>
    calculateProcessingFeeForRegistrations(registrations || [], payment);

  const handleEditAttendeesForGroup = (registrations, optionIdOverride) => {
    const firstTransactionId = getTransactionIdString(
      registrations?.[0]?.transaction_id,
    );
    const resolvedOptionId =
      optionIdOverride ||
      findSponsorshipEventOptionId(registrations) ||
      (!Array.isArray(activeTabOptionId) ? activeTabOptionId : undefined);
    if (eventRegistrationType === 'sponsorship') {
      // Original purchase and later-added-rep payments both open the sponsorship cart for that transaction.
      if (!resolvedOptionId) {
        return;
      }
      navigate(
        firstTransactionId
          ? `/eventSponsorCart/${eventId}/${resolvedOptionId}/${firstTransactionId}`
          : `/eventSponsor/${eventId}/${resolvedOptionId}`,
      );
    } else {
      const tid = getTransactionIdString(firstTransactionId);
      navigate(
        tid
          ? `/attendeesCart/${eventId}/${tid}`
          : `/addAttendees/${eventId}`,
      );
    }
  };

  const handleCompletePayment = () => {
    if (eventRegistrationType === 'sponsorship') {
      const optionId = findSponsorshipEventOptionId(attendees || sponsors);
      navigate(`/eventSponsor/${eventId}/${optionId}`);
    } else {
      navigate(`/addAttendees/${eventId}`);
    }
  };

  const handleTabKey = (key) => {
    setActiveTabKey(key);
  };

  // Matches API VENDOR_REP_CHANGE_CUTOFF_DAYS: open until 7 days before event start.
  const isWithinSponsorshipEditWindow = () => {
    if (!eventStartDate) return false;
    return dayjs().isBefore(
      dayjs(eventStartDate).subtract(VENDOR_REP_CHANGE_CUTOFF_DAYS, 'day'),
    );
  };

  const canShowEditRegistration = (
    registrations,
    transactionKey,
    {sponsorshipEditAllowed} = {},
  ) => {
    const isEventExpired = eventStartDate
      ? dayjs(eventStartDate).isBefore(dayjs(), 'minute')
      : true;
    const hasValidTransactionId = !!(
      transactionKey && transactionKey !== 'null'
    );

    let isEventStartDateValidation = false;
    if (eventRegistrationType === 'sponsorship') {
      // Prefer explicit API/group flag when provided; otherwise local 7-day cutoff.
      isEventStartDateValidation =
        typeof sponsorshipEditAllowed === 'boolean'
          ? sponsorshipEditAllowed
          : isWithinSponsorshipEditWindow();
    } else {
      const daysDifference = dayjs(eventStartDate).diff(dayjs(), 'day');
      isEventStartDateValidation = daysDifference >= 10;
    }

    if (
      isEventExpired &&
      eventRegistrationType !== EventRegistrationType.waitingList
    ) {
      return false;
    }

    return (
      hasValidTransactionId &&
      isEventStartDateValidation &&
      eventRegistrationType !== EventRegistrationType.waitingList
    );
  };

  // event cta for edit registration (Refund / Cancel stay on the card header)
  const renderButtons = (group) => {
    const isEventExpired = eventStartDate
      ? dayjs(eventStartDate).isBefore(dayjs(), 'minute')
      : true;

    const tabAttendees = group?.attendees;
    // hide the event registration if event expired and show message of event expired
    if (
      isEventExpired &&
      eventRegistrationType !== EventRegistrationType.waitingList
    ) {
      return (
        <Alert
          prefixCls='attendees-description-alert'
          message={ApplicationString['userEventDetailsModal.alertMsg.expired']}
          type='info'
          showIcon
        />
      );
    }

    const sponsorshipRefunded = tabAttendees?.some(
      (row) =>
        row?.is_sponsor &&
        (Number(row?.status) === EventRegistrationStatus.PAYMENT_REFUNDED ||
          Number(row?.status) === EventRegistrationStatus.REFUND_PENDING),
    );

    // Sponsorship / vendor-rep edit closes 7 days before the event.
    // SponsorshipGroups already shows the blue policy alert — skip the duplicate white one there.
    const sponsorshipEditClosed =
      eventRegistrationType === 'sponsorship' &&
      !sponsorshipRefunded &&
      (typeof group?.can_edit_reps === 'boolean'
        ? !group.can_edit_reps
        : !isWithinSponsorshipEditWindow());
    if (sponsorshipEditClosed) {
      if (showSponsorshipGroups) {
        return null;
      }
      return (
        <Alert
          prefixCls='attendees-description-alert'
          message={ApplicationString['sponsorshipReps.changesClosed']}
          type='info'
          showIcon
        />
      );
    }

    const buttons = [];
    if (
      !sponsorshipRefunded &&
      canShowEditRegistration(tabAttendees, group?.key, {
        sponsorshipEditAllowed:
          typeof group?.can_edit_reps === 'boolean'
            ? group.can_edit_reps
            : isWithinSponsorshipEditWindow(),
      })
    ) {
      buttons.push(
        <Button
          type='primary'
          className='common-button-refund'
          key='edit-registration'
          onClick={() =>
            handleEditAttendeesForGroup(tabAttendees, group?.optionId)
          }>
          {ApplicationString['myEventCard.editRegistration']}
        </Button>,
      );
    }
    return buttons;
  };

  const renderGroupedEditRegistrationButton = (group) => {
    const sponsorshipRefunded = group?.attendees?.some(
      (row) =>
        row?.is_sponsor &&
        (Number(row?.status) === EventRegistrationStatus.PAYMENT_REFUNDED ||
          Number(row?.status) === EventRegistrationStatus.REFUND_PENDING),
    );
    if (
      sponsorshipRefunded ||
      !canShowEditRegistration(group?.attendees, group?.key)
    ) {
      return null;
    }

    return (
      <Button
        type='primary'
        className='common-button-refund'
        onClick={() =>
          handleEditAttendeesForGroup(group?.attendees, group?.optionId)
        }>
        {ApplicationString['myEventCard.editRegistration']}
      </Button>
    );
  };

  // show the data based on event registration type
  useEffect(() => {
    let categorizedData;
    let dataWithWaitingList;

    if (eventRegistrationType === 'sponsorship') {
      categorizedData = categorizeAndGroupByTransitionId(
        sponsors,
        ApplicationString,
      );
      dataWithWaitingList = categorizedData.reverse();
    } else {
      categorizedData = categorizeAndGroupByTransitionId(
        attendees,
        ApplicationString,
      );
      dataWithWaitingList = categorizedData.reverse();
    }

    setEventRegistrationTypeData(dataWithWaitingList);
  }, [eventRegistrationType, attendees, sponsors]);

  useEffect(() => {
    if (eventRegistrationTypeData?.length > 0) {
      const tabStillExists = eventRegistrationTypeData.some(
        (group) => group?.key === activeTabKey,
      );
      if (!tabStillExists) {
        setActiveTabKey(eventRegistrationTypeData[0]?.key);
      }
    }
  }, [eventRegistrationTypeData, eventRegistrationType, activeTabKey]);

  // Sponsorship groups: refund / cancel on the card covers every registration of every sponsorship.
  useEffect(() => {
    if (showSponsorshipGroups) {
      onActiveTabAttendeesChange?.(
        event.sponsorships.flatMap((sponsorship) =>
          (sponsorship?.transactions || []).flatMap(
            (transaction) => transaction?.registrations || [],
          ),
        ),
      );
    }
  }, [showSponsorshipGroups, event?.sponsorships]);

  // set initial data for active tab
  useEffect(() => {
    if (showSponsorshipGroups) {
      return;
    }
    if (eventRegistrationTypeData?.length > 0 && activeTabKey) {
      const activeTabData = eventRegistrationTypeData?.find(
        (group) => group?.key === activeTabKey,
      );
      if (activeTabData) {
        const {totalCost, discountAmount, payableAmount} = getRegistrationCosts(
          activeTabData?.attendees,
        );
        const tidFromAttendee = getTransactionIdString(
          activeTabData?.attendees?.[0]?.transaction_id,
        );
        const transactionIdValue =
          tidFromAttendee ??
          (activeTabData?.key && activeTabData.key !== 'null'
            ? activeTabData.key
            : null);
        setData(activeTabData ? activeTabData?.attendees : []);
        setActiveTabOptionId(
          findSponsorshipEventOptionId(
            activeTabData ? activeTabData?.attendees : [],
          ),
        );
        setTransactionId(transactionIdValue);
        setActiveTabCosts({
          payableAmount: payableAmount,
          discountAmount: discountAmount,
          totalCost: totalCost,
        });
        onActiveTabAttendeesChange?.(activeTabData?.attendees || []);
      }
    } else {
      onActiveTabAttendeesChange?.([]);
    }
  }, [activeTabKey, eventRegistrationType, attendees, sponsors]);

  // set to_be_paid + payment_processing data for table (cart / unpaid event details)
  useEffect(() => {
    if (eventRegistrationType === 'sponsorship') {
      setToBePaidRegistration(filterCartStatusRegistrations(sponsors));
    } else if (eventRegistrationType === EventRegistrationType.waitingList) {
      setToBePaidRegistration(attendees);
    } else {
      setToBePaidRegistration(filterCartStatusRegistrations(attendees));
    }
  }, [sponsors, attendees, eventRegistrationType]);

  const columns =
    eventRegistrationType === EventRegistrationType.waitingList
      ? [
          {
            title: ApplicationString['userEventDetailsModal.name'],
            dataIndex: 'name',
            key: 'name',
            render: (text, record) => {
              return (
                <Tag prefixCls='regularAttendeesTag'>
                  {record.attendee_first_name} {record.attendee_last_name}
                </Tag>
              );
            },
          },
          {
            title: ApplicationString['userEventDetailsModal.email'],
            dataIndex: 'attendee_email',
            key: 'email',
            render: (text) => {
              return <Tag prefixCls='regularAttendeesTag'>{text}</Tag>;
            },
          },
          {
            title:
              ApplicationString['waitingList.attendanceType'] ||
              ApplicationString['userEventDetailsModal.attendanceType'],
            dataIndex: 'attendance_type',
            key: 'attendance_type',
            render: (_, record) => {
              return (
                <Tag prefixCls='regularAttendeesTag'>
                  {getWaitingListAttendanceLabel(record, ApplicationString)}
                </Tag>
              );
            },
          },
          {
            title: ApplicationString['userEventDetailsModal.registrationType'],
            dataIndex: 'registration_type',
            key: 'registration_type',
            hidden: !event?.is_available_for_webcast,
            render: (text) => {
              const capitalizedLetter =
                text.charAt(0).toUpperCase() + text.slice(1);
              return (
                <Tag prefixCls='regularAttendeesTag'>{capitalizedLetter}</Tag>
              );
            },
          },
          {
            title: ApplicationString['userEventDetailsModal.sponsorshipType'],
            dataIndex: 'sponsorship_type',
            key: 'sponsorship_type',
            render: (_, record) => {
              const optionName = record?.event_option_name;
              if (
                (record?.registration_type || '').toString().toLowerCase() ===
                'registration'
              ) {
                return <Tag prefixCls='regularAttendeesTag'>-</Tag>;
              }
              if (!optionName) {
                return <Tag prefixCls='regularAttendeesTag'>-</Tag>;
              }
              return <Tag prefixCls='regularAttendeesTag'>{optionName}</Tag>;
            },
          },
          {
            title: ApplicationString['userEventDetailsModal.date'],
            dataIndex: 'created_at',
            key: 'created_at',
            render: (text) => {
              if (!text) return <Tag prefixCls='regularAttendeesTag'>-</Tag>;
              return (
                <Tag prefixCls='regularAttendeesTag'>
                  {dayjs(text).format('MM/DD/YYYY')}
                </Tag>
              );
            },
          },
        ]
      : [
          {
            title: ApplicationString['userEventDetailsModal.name'],
            dataIndex: 'name',
            key: 'name',
            editable: true,
            width: '8%',
            render: (text, record) => {
              return (
                <Tag prefixCls='regularAttendeesTag'>
                  {record.attendee_first_name} {record.attendee_last_name}
                </Tag>
              );
            },
          },
          {
            title: ApplicationString['userEventDetailsModal.email'],
            dataIndex: 'attendee_email',
            key: 'email',
            width: '15%',
            editable: true,
            render: (text) => {
              return <Tag prefixCls='regularAttendeesTag'>{text}</Tag>;
            },
          },
          {
            title:
              ApplicationString['userEventDetailsModal.companyAffiliation'],
            dataIndex: 'attendee_company',
            key: 'company',
            width: '12%',
            editable: true,
            render: (text) => {
              return <Tag prefixCls='regularAttendeesTag'>{text}</Tag>;
            },
          },
          {
            title: ApplicationString['eventCommonTable.lunch'],
            dataIndex: 'lunch',
            key: 'lunch',
            width: '12%',
            render: (_, record) => {
              const food = record?.event_registration_options?.food;
              const foodName =
                food?.name || ApplicationString['sponsorshipReps.noFood'];
              const foodPrice = food?.price;
              const label =
                food?.name && (foodPrice || foodPrice === 0)
                  ? `${foodName} (${formatCurrencyByLocalChapter(
                      foodPrice,
                      eventLocalChapterData,
                    )})`
                  : foodName;
              return <Tag prefixCls='regularAttendeesTag'>{label}</Tag>;
            },
          },
          {
            title: meetingPresentationLabel(
              event?.sponsorships?.[0]?.event_sponsorship_perk
                ?.attendees_price ??
                data?.[0]?.event_registration_options?.sponsorship
                  ?.event_sponsorship_perk?.attendees_price,
              ApplicationString,
            ),
            dataIndex: 'attending_presentation',
            key: 'attending_presentation',
            width: '12%',
            hidden: eventRegistrationType !== 'sponsorship',
            render: (text) => (
              <Tag prefixCls='regularAttendeesTag'>{text ? 'Yes' : 'No'}</Tag>
            ),
          },
          {
            title: attendingDinnerLabel(
              event?.sponsorships?.[0]?.event_sponsorship_perk
                ?.dinner_with_speaker_price ??
                data?.[0]?.event_registration_options?.sponsorship
                  ?.event_sponsorship_perk?.dinner_with_speaker_price,
              ApplicationString,
            ),
            dataIndex: 'attending_dinner',
            key: 'attending_dinner',
            width: '10%',
            hidden: eventRegistrationType !== 'sponsorship',
            render: (text) => (
              <Tag prefixCls='regularAttendeesTag'>{text ? 'Yes' : 'No'}</Tag>
            ),
          },
          {
            title: ApplicationString['userEventDetailsModal.department'],
            dataIndex: 'attendee_department',
            key: 'department',
            width: '10%',
            hidden: !isJapanLocalChapter,
            render: (text) => {
              return (
                <Tag prefixCls='regularAttendeesTag'>
                  {formatAttendeeFieldOrNA(text)}
                </Tag>
              );
            },
          },
          {
            title: ApplicationString['userEventDetailsModal.jobTitle'],
            dataIndex: 'attendee_job_title',
            key: 'jobTitle',
            width: '10%',
            hidden: !isJapanLocalChapter,
            render: (text) => {
              return (
                <Tag prefixCls='regularAttendeesTag'>
                  {formatAttendeeFieldOrNA(text)}
                </Tag>
              );
            },
          },

          {
            title: ApplicationString['userEventDetailsModal.refundedAmount'],
            dataIndex: 'refunded_amount',
            width: '8%',
            render: (text) => {
              return (
                <Tag prefixCls='regularAttendeesTag'>
                  {formatCurrencyByLocalChapter(text, eventLocalChapterData)}
                </Tag>
              );
            },
          },
          {
            title: ApplicationString['userEventDetailsModal.refundType'],
            dataIndex: 'refund_to_account',
            width: '8%',
            render: (_, record) => {
              const refund_mode = record?.refund_to_account;
              const refund_status = record?.status == 0;
              return (
                <Tag prefixCls='regularAttendeesTag'>
                  {refundMode(refund_mode, refund_status)}
                </Tag>
              );
            },
          },
          {
            title:
              ApplicationString['userEventDetailsModal.registrationStatus'],
            dataIndex: 'status',
            key: 'status',
            width: '10%',
            render: (status) => {
              const {label, color} = getStatusInfoOfEventRegistration(
                status,
                ApplicationString,
              );
              return (
                <Tag
                  prefixCls='regularAttendeesTag'
                  className={`registration-status-tag registration-status-tag-${color}`}>
                  {label}
                </Tag>
              );
            },
          },
          {
            title: ApplicationString['userEventDetailsModal.attendanceType'],
            dataIndex: 'registration_type',
            key: 'registration_type',
            hidden: !event.is_online && !event.is_available_for_webcast,
            width: '12%',
            editable: true,
            render: (text) => {
              return (
                <Tag prefixCls='regularAttendeesTag'>
                  {' '}
                  {text === 'In-Person'
                    ? ApplicationString['editableCell.inPerson']
                    : text === 'Webcast'
                    ? ApplicationString['editableCell.webcast']
                    : text}
                </Tag>
              );
            },
          },
          {
            title: ApplicationString['userEventDetailsModal.cost'],
            dataIndex: 'total_cost',
            width: '5%',
            render: (text, record) => {
              const payment = resolveActiveTabPayment(
                event?.payments,
                getTransactionIdString(record?.transaction_id),
              );
              const paidAmount = getPaidAmountIncludingProcessingFee(
                record,
                payment,
              );
              return (
                <h4 className='regularAttendees-price'>
                  {formatCurrencyByLocalChapter(
                    paidAmount,
                    eventLocalChapterData,
                    2,
                    2,
                  )}
                </h4>
              );
            },
          },
        ];

  const renderTabActions = (group) => {
    const actions = renderButtons(group);
    if (!actions || (Array.isArray(actions) && actions.length === 0)) {
      return null;
    }
    return (
      <div className='modal-title-wrapper'>
        <div className='modal-title-buttons'>{actions}</div>
      </div>
    );
  };

  const noFilteredAttendees = filteredAttendees?.length < 0;

  const tabItems = eventRegistrationTypeData?.map((group) => ({
    key: group.key,
    label: group.label,
    children: (
      <>
        {eventType !== EventRegistrationType.waitingList && (
          <div className='table-edit-container'>
            <div className='table-cost-amount'>
              <p className='table-total-cost'>
                {ApplicationString['userEventDetailsModal.totalCost']}:{' '}
                {(() => {
                  const activeTabPayment = resolveActiveTabPayment(
                    event?.payments,
                    activeTabKey,
                  );
                  const payable = activeTabCosts?.payableAmount ?? 0;
                  const processingFee = activeTabPayment
                    ? getProcessingFee(data, activeTabPayment)
                    : 0;
                  const paidAmount = roundCurrency(
                    Math.max(0, payable) + processingFee,
                  );
                  return formatCurrencyByLocalChapter(
                    paidAmount,
                    eventLocalChapterData,
                    2,
                    2,
                  );
                })()}
                {(() => {
                  const activeTabPayment = resolveActiveTabPayment(
                    event?.payments,
                    activeTabKey,
                  );
                  const paymentDateTime =
                    activeTabPayment?.payment_datetime ??
                    activeTabPayment?.payment_date;
                  const payable = activeTabCosts?.payableAmount ?? 0;
                  const hasCredit = data?.[0]?.credit_amount > 0;
                  const isZeroAmount = Math.abs(payable) < 0.01 || hasCredit;

                  const registrationDate = data?.[0]?.registration_date;

                  const displayDate = isZeroAmount
                    ? registrationDate
                    : paymentDateTime;

                  const formattedDate = displayDate
                    ? formatUtcToLocalDateTime(displayDate) || 'N/A'
                    : null;
                  // Only show fee for a payment matched to this tab's transaction_id.
                  // Do not fall back to `event` — myEvents copies the latest payment's
                  // processing_fee_amount onto the event, which incorrectly attributed
                  // that fee to admin-confirmed / unmatched tabs.
                  const processingFee = activeTabPayment
                    ? getProcessingFee(data, activeTabPayment)
                    : 0;
                  return (
                    <>
                      {formattedDate && (
                        <span style={{marginLeft: '15px'}}>
                          {
                            ApplicationString[
                              'userEventDetailsModal.paymentDate'
                            ]
                          }
                          : {formattedDate}
                          {processingFee > 0 ? (
                            <span style={{marginLeft: '15px'}}>
                              {
                                ApplicationString[
                                  'userEventDetailsModal.processingFee'
                                ]
                              }
                              :{' '}
                              {formatCurrencyByLocalChapter(
                                processingFee,
                                eventLocalChapterData,
                                2,
                                2,
                              )}
                              <Tooltip
                                color='#ffffff'
                                overlayInnerStyle={{color: '#2b2b2b'}}
                                title={
                                  ApplicationString[
                                    'userEventDetailsModal.processingFeeTooltip'
                                  ]
                                }>
                                <InfoCircleOutlined
                                  style={{
                                    color: '#d98f00',
                                    fontSize: '12px',
                                    marginLeft: '4px',
                                    position: 'relative',
                                    top: '-2px',
                                  }}
                                />
                              </Tooltip>
                            </span>
                          ) : null}
                        </span>
                      )}
                    </>
                  );
                })()}
              </p>
            </div>
            {renderTabActions(group)}
          </div>
        )}
        <Table
          prefixCls='event-details-table'
          dataSource={data}
          scroll={{x: 1000}}
          columns={columns?.filter((column) => !column.hidden)}
          size='small'
          pagination={false}
        />
      </>
    ),
  }));

  return (
    <div className='modal-container'>
      <div className='event-details-modal-container'>
        {noFilteredAttendees && (
          <div key='cancel' className='update-footer'>
            <Button
              type='primary'
              className={`${'common-button-refund'}`}
              key='cancel'
              onClick={handleRefund}>
              {ApplicationString['myEventCard.askForRefund']}
            </Button>
          </div>
        )}
        <div className='evente-details-modal-wapper'>
          {/* <div>
              <p>
                <strong>Event Name:</strong> {eventName}
              </p>
              <p>
                <strong>Status of Payment:</strong> {paymentStatus}
              </p>
              {payment_mode && (
                <p>
                  <strong>Mode of Refund :</strong> {payment_mode}
                </p>
              )}
            </div> */}
          {/* <div>
              {refund_amount && (
                <p>
                  <strong>Refunded Amount:</strong> {refund_amount}
                </p>
              )}
              {credit_amount && (
                <p>
                  <strong>Credit Amount:</strong> {credit_amount}
                </p>
              )}
            </div> */}
        </div>
        {!paidEvent ||
        eventRegistrationType === EventRegistrationType.waitingList ? (
          (() => {
            if (eventRegistrationType === EventRegistrationType.waitingList) {
              return (
                <Table
                  prefixCls='event-details-table'
                  dataSource={toBePaidEventRegistration}
                  scroll={{x: 1000}}
                  columns={columns}
                  size='small'
                  pagination={false}
                />
              );
            }
            const {paymentProcessing, toBePaid} =
              splitCartRegistrationsByStatus(toBePaidEventRegistration);
            const groupedPaymentProcessing =
              groupCartPaymentProcessingRegistrations(
                paymentProcessing,
                ApplicationString,
              );
            const activeCartPaymentProcessingGroup =
              groupedPaymentProcessing?.find(
                (group) => group?.key === activeCartPaymentProcessingKey,
              ) || groupedPaymentProcessing?.[0];
            const activeCartPaymentProcessingActiveKey =
              activeCartPaymentProcessingGroup?.key;
            return (
              <div className='event-details-modal-cart-sections'>
                {groupedPaymentProcessing?.length > 0 && (
                  <Tabs
                    prefixCls='user-event-card'
                    activeKey={activeCartPaymentProcessingActiveKey}
                    onChange={(key) => setActiveCartPaymentProcessingKey(key)}
                    items={groupedPaymentProcessing?.map((group) => {
                      const editRegistrationButton =
                        renderGroupedEditRegistrationButton(group);
                      const groupCosts = getRegistrationCosts(group?.attendees);
                      const tabPayment = resolveCartProcessingTabPayment(
                        event?.payments,
                        group.key,
                      );
                      const paymentStartedAt =
                        tabPayment?.payment_datetime ??
                        tabPayment?.payment_date ??
                        event?.payment_datetime ??
                        event?.payment_date;
                      const payableProc = groupCosts?.payableAmount ?? 0;
                      const hasCreditProc = group?.attendees?.some(
                        (a) => Number(a?.credit_amount) > 0,
                      );
                      const isZeroProc =
                        Math.abs(payableProc) < 0.01 || hasCreditProc;
                      const regDateProc =
                        group?.attendees?.[0]?.registration_date;
                      const displayProcDate = isZeroProc
                        ? regDateProc
                        : paymentStartedAt;
                      const formattedProcDate = displayProcDate
                        ? formatUtcToLocalDateTime(displayProcDate) || null
                        : null;
                      const processingFee = getProcessingFee(
                        group?.attendees,
                        tabPayment || event,
                      );

                      return {
                        key: group.key,
                        label: group.label,
                        children: (
                          <>
                            <div className='table-edit-container'>
                              <div className='table-cost-amount'>
                                <p className='table-total-cost'>
                                  {
                                    ApplicationString[
                                      'userEventDetailsModal.totalCost'
                                    ]
                                  }
                                  :{' '}
                                  {formatCurrencyByLocalChapter(
                                    roundCurrency(
                                      Math.max(
                                        0,
                                        groupCosts?.payableAmount ?? 0,
                                      ) + processingFee,
                                    ),
                                    eventLocalChapterData,
                                    2,
                                    2,
                                  )}
                                  {formattedProcDate ? (
                                    <span style={{marginLeft: '15px'}}>
                                      {
                                        ApplicationString[
                                          'userEventDetailsModal.paymentDate'
                                        ]
                                      }
                                      : {formattedProcDate}
                                      {processingFee > 0 ? (
                                        <span style={{marginLeft: '15px'}}>
                                          {
                                            ApplicationString[
                                              'userEventDetailsModal.processingFee'
                                            ]
                                          }
                                          :{' '}
                                          {formatCurrencyByLocalChapter(
                                            processingFee,
                                            eventLocalChapterData,
                                            2,
                                            2,
                                          )}
                                          <Tooltip
                                            color='#ffffff'
                                            overlayInnerStyle={{
                                              color: '#2b2b2b',
                                            }}
                                            title={
                                              ApplicationString[
                                                'userEventDetailsModal.processingFeeTooltip'
                                              ]
                                            }>
                                            <InfoCircleOutlined
                                              style={{
                                                color: '#d98f00',
                                                fontSize: '12px',
                                                marginLeft: '4px',
                                                position: 'relative',
                                                top: '-2px',
                                              }}
                                            />
                                          </Tooltip>
                                        </span>
                                      ) : null}
                                    </span>
                                  ) : null}
                                </p>
                              </div>
                              {editRegistrationButton && (
                                <div className='modal-title-buttons'>
                                  {editRegistrationButton}
                                </div>
                              )}
                            </div>
                            <Table
                              prefixCls='event-details-table'
                              dataSource={group.attendees}
                              scroll={{x: 1000}}
                              columns={columns?.filter(
                                (column) => !column.hidden,
                              )}
                              size='small'
                              pagination={false}
                            />
                          </>
                        ),
                      };
                    })}
                  />
                )}
                {toBePaid?.length > 0 && (
                  <div className='event-details-cart-section'>
                    <div className='event-details-cart-section-header'>
                      {ApplicationString['userEventDetailsModal.totalCost']}:{' '}
                      {formatCurrencyByLocalChapter(
                        toBePaid?.reduce(
                          (sum, attendee) =>
                            sum +
                            getPaidAmountIncludingProcessingFee(attendee, null),
                          0,
                        ),
                        eventLocalChapterData,
                        2,
                        2,
                      )}
                    </div>
                    <Table
                      prefixCls='event-details-table'
                      dataSource={toBePaid}
                      scroll={{x: 1000}}
                      columns={columns?.filter((column) => !column.hidden)}
                      size='small'
                      pagination={false}
                    />
                  </div>
                )}
              </div>
            );
          })()
        ) : showSponsorshipGroups ? (
          <SponsorshipGroups
            sponsorships={event?.sponsorships}
            columns={columns}
            eventId={eventId}
            eventLocalChapterData={eventLocalChapterData}
            isJapanLocalChapter={isJapanLocalChapter}
            renderTransactionActions={renderTabActions}
            onRefresh={onRefresh}
          />
        ) : (
          <Tabs
            prefixCls='user-event-card'
            activeKey={activeTabKey}
            onChange={(key) => handleTabKey(key)}
            items={tabItems}
          />
        )}
      </div>
    </div>
  );
};

export default EventDetailsModal;
